import { ImagesEntity } from '@galabid/gb-dashboard/shared/data-access-images'
import { ProjectorsEntity } from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.models'
import {
  CountdownWidgetModel,
  HonourRollWidgetModel,
  ImageWidgetModel,
  ItemTableWidgetModel,
  NotificationWidgetModel,
  QrCodeWidgetModel,
  RaffleDrawWidgetModel,
  RaffleWinnersWidgetModel,
  SmartTextWidgetModel,
  TeamScoringWidgetModel,
  PowerPledgeWidgetModel,
  TextWidgetModel,
  ThermometerWidgetModel,
  TotalRaisedWidgetModel,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { ProjectorTemplateRawConfig } from './+state/projector-templates.models'

export interface ProjectorTemplateConfigBuildInput {
  projector?: ProjectorsEntity | null
  backgroundImage?: ImagesEntity | null
  countdownWidgets: CountdownWidgetModel[]
  imageWidgets: ImageWidgetModel[]
  textWidgets: TextWidgetModel[]
  smartTextWidgets: SmartTextWidgetModel[]
  totalRaisedWidgets: TotalRaisedWidgetModel[]
  teamScoringWidgets: TeamScoringWidgetModel[]
  powerPledgeWidgets: PowerPledgeWidgetModel[]
  notificationWidgets: NotificationWidgetModel[]
  itemTableWidgets: ItemTableWidgetModel[]
  raffleWinnersWidgets: RaffleWinnersWidgetModel[]
  honourRollWidgets: HonourRollWidgetModel[]
  thermometerWidgets: ThermometerWidgetModel[]
  raffleDrawWidgets: RaffleDrawWidgetModel[]
  qrCodeWidgets: QrCodeWidgetModel[]
}

// this is for encoding the template - getting what the user has designed and getting it ready for the db
// not for getting the config from the db and decoding it (that's handled in the effects)

type ImageIdCarrier = ImagesEntity | { id?: unknown } | null | undefined

export function buildProjectorTemplateConfig(input: ProjectorTemplateConfigBuildInput): string {
  const { projector } = input

  const getImageId = (image: ImageIdCarrier): number | null => {
    const id = image?.id
    return id ? Number(id) : null
  }

  type ApiWidget = { uuid: string; id?: number; projector_id?: number | null }

  const mapToApi = <T extends ApiWidget>(list: T[], overrides?: (w: T) => Partial<T>): T[] => {
    return list.map((w) => ({
      ...w,
      id: 0,
      projector_id: null,
      // TODO GB-11987 images will need specific logic here
      ...(overrides ? overrides(w) : {}),
    }))
  }

  const payload: ProjectorTemplateRawConfig = {
    Projector: {
      id: 0,
      uuid: projector?.uuid ?? 'template',
      is_active: projector?.is_active ?? false,
      auction_id: null,
      name: projector?.name ?? 'template',
      background_image: projector?.background_image?.uuid ?? '',
      background_color: projector?.background_color ?? '',
      custom_css: projector?.custom_css ?? '',
      last_notification_time: null,
      background_image_cover: projector?.background_image_cover ?? true,
      font_family: projector?.font_family ?? null,
      color: projector?.color ?? null,
    },
    TextProjectorWidget: mapToApi(input.textWidgets),
    SmartTextProjectorWidget: mapToApi(input.smartTextWidgets),
    ItemTableProjectorWidget: mapToApi(input.itemTableWidgets),
    NotificationProjectorWidget: mapToApi(input.notificationWidgets),
    TotalRaisedProjectorWidget: mapToApi(input.totalRaisedWidgets),
    RaffleDrawProjectorWidget: mapToApi(input.raffleDrawWidgets),
    RaffleWinnersProjectorWidget: mapToApi(input.raffleWinnersWidgets),
    CountDownProjectorWidget: mapToApi(input.countdownWidgets),
    TeamScoringProjectorWidget: mapToApi(input.teamScoringWidgets),
    PowerPledgeProjectorWidget: mapToApi(input.powerPledgeWidgets),
    HonourRollProjectorWidget: mapToApi(input.honourRollWidgets),

    ImageProjectorWidget: mapToApi<ImageWidgetModel>(input.imageWidgets, (w) => ({
      image_id: getImageId(w.image),
    })),
    ThermometerProjectorWidget: mapToApi<ThermometerWidgetModel>(input.thermometerWidgets, (w) => ({
      custom_image_id: getImageId(w.custom_image),
    })),
    QrCodeProjectorWidget: mapToApi(input.qrCodeWidgets),
  }

  return JSON.stringify(payload)
}
